<!--
  The saved Equipment Safety Inspection & Repair Reports.

  A list and a "New inspection" button, nothing else: the sheet itself is the
  form on the next screen (Task 6). Follows MixDesignList.svelte's structure.

  The repair count is shown plainly because it's what a reader of this form
  cares about, but it is never coloured as a pass/fail pill or called a
  verdict — this form judges nothing.

  Per-site: with `?site=`, this is one site's inspections, gathered by the
  day they were done (newest first) — a site that has run for months is
  otherwise one long list where every entry looks alike. `?date=` drills one
  level further, into that day's machines. Without a site, every inspection
  across every site, flat, newest first — the grouped-by-day view only makes
  sense once you already know which site you are looking at.
-->
<script lang="ts">
  import { linkHandler, location, navigate, routePath, siteIdFromQuery } from '../../lib/router';
  import {
    equipmentInspections,
    equipmentInspectionsLoading,
    loadEquipmentInspections
  } from '../../lib/store';

  const siteId = $derived(siteIdFromQuery($location.query));

  $effect(() => {
    void loadEquipmentInspections(siteId);
  });

  /** Newest by the record's own date, falling back to when it was last touched. */
  const sorted = $derived(
    [...$equipmentInspections].sort((a, b) =>
      a.inspection_date === b.inspection_date
        ? b.updated_at.localeCompare(a.updated_at)
        : b.inspection_date.localeCompare(a.inspection_date)
    )
  );

  /**
   * The date being viewed inside a site, from `?date=`.
   *
   * Inspections drill one level further than the other forms: a site holds
   * several days, and a day holds one record per machine. Without the date
   * level, a site that has run for months is one long list where every entry
   * looks alike.
   */
  const selectedDate = $derived(($location.query.get('date') ?? '').trim());

  /** The site's inspections gathered by the day they were done, newest first. */
  const siteDates = $derived.by(() => {
    const days = new Map<string, typeof sorted>();
    for (const record of sorted) {
      const key = (record.inspection_date ?? '').trim();
      const bucket = days.get(key);
      if (bucket) bucket.push(record);
      else days.set(key, [record]);
    }
    return [...days.entries()]
      .map(([date, records]) => ({ date, records }))
      .sort((a, b) => {
        // Undated last, rather than sorting as though they were ancient.
        if (!a.date) return 1;
        if (!b.date) return -1;
        return b.date.localeCompare(a.date);
      });
  });

  /**
   * One day's inspections, ordered by equipment number — the thing you are
   * actually picking between once you have chosen a site and a day.
   */
  const dateRecords = $derived(
    selectedDate
      ? [...(siteDates.find((d) => d.date === selectedDate)?.records ?? [])].sort((a, b) =>
          (a.equipment_no ?? '').localeCompare(b.equipment_no ?? '', undefined, { numeric: true })
        )
      : []
  );
</script>

<div class="card">
  <div class="card__body">
    {#if siteId !== null && selectedDate}
      <a
        class="btn btn--sm"
        href={routePath.qualityEquipmentList(siteId)}
        onclick={linkHandler}
      >&#8592; All dates</a>
      <h1 class="card__title">
        Equipment Safety Inspection <span class="meta">&middot; {selectedDate}</span>
      </h1>
      <!--
        Creating from inside a day carries the day with it too, so the date is
        already right and only the machine has to be typed.
      -->
      <button
        class="btn btn--primary btn--block"
        type="button"
        onclick={() => navigate(routePath.newQualityEquipment(siteId, selectedDate))}
      >
        New inspection on {selectedDate}
      </button>
    {:else if siteId !== null}
      <a class="btn btn--sm" href={routePath.qualitySite(siteId)} onclick={linkHandler}>&#8592; Back to site</a>
      <h1 class="card__title">Equipment Safety Inspection &amp; Repair Report</h1>
      <button
        class="btn btn--primary btn--block"
        type="button"
        onclick={() => navigate(routePath.newQualityEquipment(siteId))}
      >
        New inspection
      </button>
    {:else}
      <a class="btn btn--sm" href={routePath.quality()} onclick={linkHandler}>&#8592; All sites</a>
      <h1 class="card__title">Equipment Safety Inspection &amp; Repair Report</h1>
      <button
        class="btn btn--primary btn--block"
        type="button"
        onclick={() => navigate(routePath.newQualityEquipment())}
      >
        New inspection
      </button>
    {/if}
  </div>
</div>

{#if $equipmentInspectionsLoading && $equipmentInspections.length === 0}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else if sorted.length === 0}
  <div class="card">
    <div class="card__body">
      <p class="meta">
        No inspections saved yet. The first one starts from the standard SCI checklist.
      </p>
    </div>
  </div>
{:else if siteId !== null && selectedDate}
  <!-- One day of a site: pick the machine. -->
  <div class="card">
    <div class="card__body">
      {#if dateRecords.length === 0}
        <p class="meta">Nothing inspected on {selectedDate}.</p>
      {:else}
        <ul class="qlist">
          {#each dateRecords as record (record.id)}
            <li>
              <a href={routePath.qualityEquipment(record.id)} onclick={linkHandler}>
                <strong>{record.equipment_no || 'Unlabelled equipment'}</strong>
                <span class="meta">
                  {record.rr_count} item{record.rr_count === 1 ? '' : 's'} needing repair
                </span>
              </a>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  </div>
{:else if siteId !== null}
  <!--
    Inside one site: the days it was inspected on, newest first. A site that
    has run for months is otherwise one long list where every entry looks
    alike; the day is what someone actually remembers.
  -->
  <div class="card">
    <div class="card__body">
      <ul class="qlist">
        {#each siteDates as bucket (bucket.date)}
          <li>
            <a
              href={routePath.qualityEquipmentList(siteId, bucket.date)}
              onclick={linkHandler}
            >
              <strong>{bucket.date || 'No date recorded'}</strong>
              <span class="meta">
                {bucket.records.length} inspection{bucket.records.length === 1 ? '' : 's'} ·
                {bucket.records.map((r) => r.equipment_no || '—').join(', ')}
              </span>
            </a>
          </li>
        {/each}
      </ul>
    </div>
  </div>
{:else}
  <!-- No site chosen: every inspection across every site, flat, newest first. -->
  <div class="card">
    <div class="card__body">
      <ul class="qlist">
        {#each sorted as record (record.id)}
          <li>
            <a href={routePath.qualityEquipment(record.id)} onclick={linkHandler}>
              <strong>{record.inspection_date || 'No date recorded'}</strong>
              <span class="meta">{record.job_name || 'No job name recorded'}</span>
              <span class="meta">
                {record.equipment_no || 'Unlabelled equipment'} ·
                {record.rr_count} item{record.rr_count === 1 ? '' : 's'} needing repair
              </span>
            </a>
          </li>
        {/each}
      </ul>
    </div>
  </div>
{/if}

<style>
  .qlist {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .qlist li + li {
    border-top: 1px solid var(--line-soft);
  }

  .qlist a {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding: 0.85rem 0;
    color: inherit;
    text-decoration: none;
    /* Tapped with gloves on, same as the field screens. */
    min-height: var(--tap);
  }

  .qlist a:hover strong {
    text-decoration: underline;
  }
</style>
